/* eslint-disable react/no-danger */
import React, { useMemo, useState } from 'react';
import { arrayOf, number, func, shape, string, object } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { REGULAR_CLASS, COURSE } from 'constants/contentTypes';
import { SUBSCRIPTION } from 'constants/checkoutCollectionGroupTypes';
import { formatPriceWithCurrency } from 'utils/price';
import useStyles from 'hooks/useStyles';
import useGetCurrentDevice from 'hooks/useGetCurrentDevice';
import { CloseIcon, NextIcon } from 'components/Icon';
import FullMask from 'components/FullMask';
import Button from 'components/Button';
import ContentInfo from 'components/ContentInfo';

import styles from './BundleCart.scss';

const renderSelectHint = ({
  t,
  price,
  isEnough,
  planType,
  itemCount,
  minimumItemCount,
  billingCyclePerMonths,
  TagName,
}) => {
  return isEnough ? (
    <TagName
      dangerouslySetInnerHTML={{
        __html:
          planType === SUBSCRIPTION
            ? t('purchase.pricingDisplay.bundleCart.priceNoMonth', {
                itemCount,
                priceNoMonth: t('purchase.pricingDisplay.priceWithMonth', {
                  perMonth:
                    billingCyclePerMonths > 1 ? billingCyclePerMonths : '',
                  price,
                }),
              })
            : t('purchase.pricingDisplay.bundleCart.priceNoMonth', {
                priceNoMonth: price,
                itemCount,
              }),
      }}
    />
  ) : (
    <TagName
      dangerouslySetInnerHTML={{
        __html: t('purchase.pricingDisplay.bundleCart.moreItems', {
          itemCount,
          minimumItemCount,
        }),
      }}
    />
  );
};

export default function BundleCart({
  planType,
  billingCyclePerMonths,
  currency,
  contentType,
  displayCurrency,
  items,
  matchedOption,
  onRemove,
  onCheckout,
  title,
  minimumItemCount,
}) {
  useStyles(styles);
  const { t } = useTranslation(['purchase']);
  const { isMobile } = useGetCurrentDevice();

  const [showModal, setShowModal] = useState(false);

  const originalTotalDisplayPrice = useMemo(() => {
    const totalPrice = items.reduce((total, { price }) => total + price, 0);
    return formatPriceWithCurrency(currency, displayCurrency, totalPrice);
  }, [currency, displayCurrency, items]);

  const toggleModal = () => setShowModal(state => !state);

  const renderItem = ({
    id,
    type,
    name,
    thumbnailUrl,
    frequency,
    lessonCount,
    totalLessonsDurationTime,
  }) => (
    <li key={`${type}-${id}`} className={styles.item}>
      <div className={styles.thumbnail}>
        <img className={styles.image} src={thumbnailUrl} alt={name} />
      </div>
      <div className={styles.information}>
        <div>{name}</div>
        {contentType === REGULAR_CLASS && (
          <p className={styles.frequency}>{frequency}</p>
        )}
        {contentType === COURSE && (
          <ContentInfo
            contentType={COURSE}
            lessonCount={lessonCount}
            totalLessonsDurationTime={totalLessonsDurationTime}
          />
        )}
        <div className={styles.removeText} onClick={onRemove(type, id)}>
          {t('purchase.content.bundlePage.removeCta')}
        </div>
      </div>

      <CloseIcon
        onClick={onRemove(type, id)}
        width={16}
        height={16}
        className={styles.removeButton}
      />
    </li>
  );

  const renderContent = () => {
    if (showModal)
      return (
        <FullMask onClickShadow={toggleModal}>
          <div className={styles.modalWrapper}>
            <div className={styles.modalInner}>
              <div className={styles.modalHeader}>
                <NextIcon width={24} height={24} onClick={toggleModal} />
              </div>
              <div className={styles.modalBody}>
                <h4 className={styles.bundleTitle}>{title}</h4>
                {isMobile &&
                  renderSelectHint({
                    TagName: 'h5',
                    isEnough: items.length >= minimumItemCount,
                    itemCount: items.length,
                    price:
                      matchedOption?.displayPrice.sellingPrice ||
                      originalTotalDisplayPrice,
                    billingCyclePerMonths,
                    minimumItemCount,
                    planType,
                    t,
                  })}
                <ul className={styles.itemList}>{items.map(renderItem)}</ul>
              </div>
              <div className={styles.modalFooter}>
                {!isMobile &&
                  renderSelectHint({
                    TagName: 'h5',
                    isEnough: items.length >= minimumItemCount,
                    itemCount: items.length,
                    price:
                      matchedOption?.displayPrice.sellingPrice ||
                      originalTotalDisplayPrice,
                    billingCyclePerMonths,
                    minimumItemCount,
                    planType,
                    t,
                  })}
                <Button
                  onClick={onCheckout(matchedOption?.id)}
                  disabled={!matchedOption}
                  text={t('purchase.content.cta.checkout')}
                  block
                />
              </div>
            </div>
          </div>
        </FullMask>
      );

    return (
      <div className={styles.content}>
        <ul className={styles.itemList}>{items.map(renderItem)}</ul>
        <Button
          onClick={onCheckout(matchedOption?.id)}
          disabled={!matchedOption}
          className={styles.checkoutButton}
          text={t('purchase.content.cta.checkout')}
        />
      </div>
    );
  };

  const isEmpty = items.length === 0;

  if (isEmpty) {
    return (
      <div className={styles.root}>
        <div className={styles.title}>
          <h5
            dangerouslySetInnerHTML={{
              __html: t('purchase.pricingDisplay.bundleCart.moreItems', {
                itemCount: items.length,
                minimumItemCount,
              }),
            }}
          />
          <Button
            text={t('purchase.content.cta.checkout')}
            disabled
            onClick={onCheckout(matchedOption?.id)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.title}>
        <div>
          {renderSelectHint({
            TagName: 'h5',
            isEnough: items.length >= minimumItemCount,
            itemCount: items.length,
            price:
              matchedOption?.displayPrice.sellingPrice ||
              originalTotalDisplayPrice,
            billingCyclePerMonths,
            minimumItemCount,
            planType,
            t,
          })}
          <div onClick={toggleModal} className={styles.modalToggler}>
            {t('purchase.content.bundlePage.viewItemsCta')}
          </div>
        </div>

        <Button
          className={styles.tabletCheckoutButton}
          onClick={onCheckout(matchedOption?.id)}
          disabled={!matchedOption}
          text={t('purchase.content.cta.checkout')}
        />
      </div>
      {renderContent()}
    </div>
  );
}

BundleCart.propTypes = {
  planType: string.isRequired,
  billingCyclePerMonths: number.isRequired,
  currency: string.isRequired,
  displayCurrency: string.isRequired,
  items: arrayOf(
    shape({
      type: string,
      id: number,
      name: string,
      description: string,
      price: number,
      displayPrice: string,
      thumbnailUrl: string,
    }),
  ).isRequired,
  title: string.isRequired,
  minimumItemCount: number.isRequired,
  onRemove: func.isRequired,
  onCheckout: func.isRequired,
  contentType: string.isRequired,
  matchedOption: object,
};

BundleCart.defaultProps = {
  matchedOption: undefined,
};
